import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { push } from "connected-react-router";

// Material UI
import { Box, Button, Typography } from "@mui/material";

// Custom Components
import Table from "../../components/Table/Table";
import { Tooltip as CustomTooltip } from "../../components/Tooltip";
import GenericAvatar from "../../components/Icons/GenericAvatar";
import AdministrationInfoLayout from "../../components/Layouts/AdministrationInfoLayout";
import GroupAverages from "./GroupAverages";
import LoadingButton from "../../components/Buttons/LoadingButton";
import { Delete } from "@mui/icons-material";
import EditGroupModal from "./EditGroupModal";
import useModal from "../../hooks/useModal";
import TooltipButton from "../../components/Buttons/TooltipButton";
import { packageNames } from "../../constants";
import GroupMembersTable from "./GroupMembersTable";

// Routes
import {
  buildGroupManagePositionsRoute,
  buildGroupManageLocationsRoute,
  buildGroupManageMembersRoute,
  buildGroupDetailLeadersRoute
} from "../../routes/AdminRoutes";

//Hooks
import { useUsers } from "../../hooks/apiHooks/users";
import useUser from "../../hooks/useUser";
import useScopedTranslation from "../../hooks/useScopedTranslation";

const locationsColumns = (t, isClient) => {
  const divisionColumn = {
    title: "OwnedBy",
    field: "customer.name"
  };
  const columns = [
    {
      title: t("employeeDetail.name"),
      field: "name"
    },
    {
      title: t("administration.location"),
      field: "state",
      render: ({ city, state, zip }) => {
        return `${city}, ${state} ${zip}`;
      }
    }
  ];
  isClient && columns.push(divisionColumn);
  return columns;
};

const positionsColumns = (t, isClient) => {
  const divisionColumn = {
    title: "OwnedBy",
    field: "customer.name"
  };
  const columns = [
    {
      title: t("employeeDetail.name"),
      field: "name"
    }
  ];
  isClient && columns.push(divisionColumn);
  return columns;
};

const leadersColumns = t => [
  {
    title: t("employeeDetail.name"),
    field: "name"
  },
  {
    title: t("administration.position"),
    field: "position.name"
  },
  {
    title: t("administration.location"),
    field: "location.name"
  }
];

const tableMaxBodyHeight = 300;

const GroupInformation = ({
  group,
  groupId,
  isLoading,
  isDeleting,
  onDeleteGroup,
  onEditGroup
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const localT = useScopedTranslation("administration.groupInformation");
  const [disabledDelete, setDisabledDelete] = useState(false);
  const [groupLeaders, setGroupLeaders] = useState([]);
  const { getUsers } = useUsers();
  const openModal = useModal(EditGroupModal);
  const taxCreditSeriesPartialSubscription =
    useUser().permissions.taxCreditSeriesPartialSubscription &&
    group?.coursePackageSubscription === packageNames.taxCreditSeries;
  const { isClient } = useUser();
  let deleteTooltipText = "";
  if (taxCreditSeriesPartialSubscription) {
    deleteTooltipText = localT(
      "taxCreditSeriesPartialSubscription.deleteGroupWarningMsg"
    );
  } else if (group && group.coursePackageSubscription) {
    deleteTooltipText = localT("deleteGroupWarning.coursePackageSubscription", {
      package: group.coursePackageSubscription
    });
  } else if (disabledDelete) {
    deleteTooltipText = localT("deleteGroupWarning.generic");
  }

  useEffect(() => {
    if (Object.keys(group).length !== 0) {
      getUsers({
        filters: {
          active: true,
          groupLeadersFor: [groupId],
          divisionId: group.customer?.id
        },
        fields: {
          user: ["name", "position", "location"],
          position: ["name"],
          location: ["name"]
        },
        include: ["location", "position"],
        sort: "name"
      }).then(response => {
        const users = response.data;
        setGroupLeaders(users);
      });
    }
  }, [group]);

  useEffect(() => {
    if (group) {
      setDisabledDelete(
        group.locations?.length !== 0 ||
          group.positions?.length !== 0 ||
          group.leaders?.length !== 0 ||
          group.employeesCount !== 0 ||
          group.assignmentsCount !== 0
      );
    }
  }, [group]);

  const deleteButton = (
    <Box flex={1} display="flex" justifyContent="flex-end" alignItems="center">
      <LoadingButton
        startIcon={<Delete />}
        pending={isDeleting}
        color="primary"
        variant="contained"
        onClick={() => onDeleteGroup(groupId)}
        disabled={
          !group ||
          disabledDelete ||
          group.coursePackageSubscription ||
          isDeleting
        }
      >
        {t("administration.deleteGroupBtn")}
      </LoadingButton>
    </Box>
  );

  const handleManagePositionsClick = () => {
    dispatch(push(buildGroupManagePositionsRoute(groupId)));
  };

  const handleManageLocationsClick = () => {
    dispatch(push(buildGroupManageLocationsRoute(groupId)));
  };
  const handleManageLeadersClick = () => {
    dispatch(push(buildGroupDetailLeadersRoute(groupId)));
  };
  const handleManageMembersClick = () => {
    dispatch(push(buildGroupManageMembersRoute(groupId)));
  };

  return (
    <AdministrationInfoLayout
      name={group ? group.name : ""}
      image={<GenericAvatar icon="group" />}
      subtitle1={
        <Box display="flex" flexDirection="column">
          <Typography
            variant="subtitle1"
            color="textSecondary"
            sx={{ pb: 0.75 }}
          >
            {t("administration.locations")}:{" "}
            {group && group.locations ? group.locations.length : 0}
          </Typography>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            sx={{ pb: 0.75 }}
          >
            {t("administration.positions")}:{" "}
            {group && group.positions ? group.positions.length : 0}
          </Typography>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            sx={{ pb: 0.75 }}
          >
            {t("administration.employees")}: {group ? group.employeesCount : 0}
          </Typography>
          <Typography
            variant="subtitle1"
            color="textSecondary"
            sx={{ pb: 0.75 }}
          >
            {t("administration.assignments")}:{" "}
            {group ? group.assignmentsCount : 0}
          </Typography>
        </Box>
      }
      infoActions={
        <TooltipButton
          tooltipProps={{
            title: taxCreditSeriesPartialSubscription
              ? localT("taxCreditSeriesPartialSubscription.editGroupWarningMsg")
              : ""
          }}
          disabled={taxCreditSeriesPartialSubscription}
          onClick={() =>
            openModal({
              group: group,
              onEditGroup: onEditGroup
            })
          }
        >
          {t("groupsFilters.editGroup")}
        </TooltipButton>
      }
      compliance={group ? group.compliance : 0}
      completedTraining={group ? group.completedTraining : 0}
      trainingHours={group ? group.trainingHours : 0}
      averageScore={group ? group.averageScore : 0}
      averages={<GroupAverages groupId={groupId} />}
      tables={[
        {
          header: t("administration.locations"),
          table: (
            <Table
              itemType="record"
              columns={locationsColumns(t, isClient)}
              data={group ? group.locations : []}
              hidePagination={true}
              maxBodyHeight={tableMaxBodyHeight}
            />
          ),
          actions: (
            <TooltipButton
              tooltipProps={{
                title: taxCreditSeriesPartialSubscription
                  ? localT(
                      "taxCreditSeriesPartialSubscription.editLocationsWarningMsg"
                    )
                  : ""
              }}
              disabled={taxCreditSeriesPartialSubscription || isLoading}
              onClick={() => handleManageLocationsClick()}
              data-qa="group-manage-locations-btn"
            >
              {t("administration.manageLocationsBtn")}
            </TooltipButton>
          )
        },
        {
          header: t("administration.positions"),
          table: (
            <Table
              itemType="record"
              columns={positionsColumns(t, isClient)}
              data={group ? group.positions : []}
              hidePagination={true}
              maxBodyHeight={tableMaxBodyHeight}
            />
          ),
          actions: (
            <Button
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={() => handleManagePositionsClick()}
              data-qa="group-manage-positions-btn"
            >
              {t("administration.managePositionsBtn")}
            </Button>
          )
        },
        {
          header: localT("members"),
          table: (
            <GroupMembersTable
              groupId={groupId}
              maxBodyHeight={tableMaxBodyHeight}
            />
          ),
          actions: (
            <TooltipButton
              tooltipProps={{
                title: taxCreditSeriesPartialSubscription
                  ? localT(
                      "taxCreditSeriesPartialSubscription.editMembersWarningMsg"
                    )
                  : isClient
                  ? localT("clientEditMembersWarningMsg")
                  : ""
              }}
              disabled={
                taxCreditSeriesPartialSubscription || isClient || isLoading
              }
              onClick={() => handleManageMembersClick()}
              data-qa="group-manage-members-btn"
            >
              {localT("manageMembersBtn")}
            </TooltipButton>
          )
        },
        {
          header: t("administration.leaders"),
          table: (
            <Table
              itemType="record"
              columns={leadersColumns(t)}
              data={group ? groupLeaders : []}
              hidePagination={true}
              maxBodyHeight={tableMaxBodyHeight}
            />
          ),
          actions: (
            <Button
              color="primary"
              onClick={handleManageLeadersClick}
              variant="contained"
            >
              {t("administration.manageLeaders")}
            </Button>
          )
        }
      ]}
      actions={
        deleteTooltipText ? (
          <CustomTooltip
            title={
              <Typography variant="body2" color="textSecondary">
                {deleteTooltipText}
              </Typography>
            }
          >
            <span>{deleteButton}</span>
          </CustomTooltip>
        ) : (
          deleteButton
        )
      }
      isLoadingInfo={isLoading}
    />
  );
};

export default GroupInformation;
